import React, { useState, useEffect, useRef } from "react";
import { apiClient, getServiceUrl } from "../utils/apiClient";
import {
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  SxProps,
  Theme,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RefreshIcon from "@mui/icons-material/Refresh";
import { EmailMessageComponent } from "../internal/Email/EmailMessageComponent";
import { DownloadFileButton } from "../internal/components/DownloadButton";
import {
  EmailGetThreadResponse,
  EmailMessage,
  EmailMetadataByIdResponse,
} from "../../../src/documents/email/types";
import { useSnackbar } from "./SnackbarProvider";
import { useSelector } from "react-redux";
import { RootState } from "../store/rootReducer";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AdminEmailPanel } from "../internal/Email/AdminEmailPanel";

interface EmailThreadComponentProps {
  emailId: string;
  sx?: SxProps<Theme>;
}

export const EmailThreadComponent: React.FC<EmailThreadComponentProps> = ({
  emailId,
  sx,
}) => {
  const [thread, setThread] = useState<EmailGetThreadResponse | null>(null);
  const [initialThreadLoading, setInitialThreadLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedForwardedEmail, setSelectedForwardedEmail] =
    useState<EmailMessage | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [emailMetadata, setEmailMetadata] =
    useState<EmailMetadataByIdResponse | null>(null);

  // Track which message indices have been loaded to prevent duplicate fetches
  const loadedMessagesRef = useRef<Set<number>>(new Set());

  // Get user data from Redux store to check admin status
  const user = useSelector((state: RootState) => state.userData.user);
  const { showSnackbar } = useSnackbar();

  // Copy email metadata to clipboard
  const copyMetadata = async () => {
    if (!currentMessage || !emailMetadata) {
      showSnackbar("No email metadata available to copy", 2, "error");
      return;
    }

    const metadata = {
      id: currentMessage.id,
      headerId: currentMessage.headerMessageId,
      threadId: currentMessage.threadId,
      messageId: currentMessage.messageId,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(metadata, null, 2));
      showSnackbar("Email metadata copied to clipboard!", 2, "info");
    } catch (err) {
      console.error("Failed to copy metadata:", err);
      showSnackbar("Failed to copy metadata to clipboard", 2, "error");
    }
  };

  useEffect(() => {
    const fetchEmailMetadata = async () => {
      try {
        setError(null);
        setInitialThreadLoading(true);

        // First, get the email metadata
        const metadataResponse = await apiClient.get<EmailMetadataByIdResponse>(
          `/documents/email/metadata/${emailId}`,
          { withCredentials: true }
        );

        setEmailMetadata(metadataResponse);

        // Then fetch the thread using new stored endpoint
        const url = `/documents/email/stored/thread/${metadataResponse.threadId}?inboxEmail=${encodeURIComponent(metadataResponse.inboxEmail)}`;
        const response = await apiClient.get<EmailGetThreadResponse>(url, {
          withCredentials: true,
        });

        const data = response;
        // Messages are already sorted by date descending from the API
        setThread(data);

        // If we have a specific messageId, jump to that message
        if (
          metadataResponse.messageId &&
          data.messages &&
          data.messages.length > 0
        ) {
          const index = data.messages.findIndex(
            (msg) => msg.messageId === metadataResponse.messageId
          );
          setCurrentIndex(index !== -1 ? index : 0);
        } else {
          setCurrentIndex(0);
        }
      } catch (err) {
        setError(String(err));
      } finally {
        setInitialThreadLoading(false);
      }
    };

    if (emailId) {
      // Reset loaded messages when emailId changes
      loadedMessagesRef.current = new Set();
      fetchEmailMetadata();
    }
  }, [emailId]);

  // Auto-load message content when currentIndex changes or thread is loaded
  useEffect(() => {
    if (thread && thread.messages && thread.messages[currentIndex]) {
      const currentMessage = thread.messages[currentIndex];
      // Check if message content needs to be loaded and hasn't been loaded/loading before
      if (
        !currentMessage.body &&
        !currentMessage.htmlBody &&
        !(currentMessage as any)._loading &&
        !loadedMessagesRef.current.has(currentIndex)
      ) {
        // Mark as loaded BEFORE calling loadMessageContent to prevent duplicate calls
        loadedMessagesRef.current.add(currentIndex);
        loadMessageContent(currentIndex);
      }
    }
  }, [currentIndex, thread?.messages?.length]); // Depend on messages length instead of thread object

  const loadMessageContent = async (messageIndex: number, isRetry = false) => {
    if (!thread || !thread.messages || messageIndex >= thread.messages.length) {
      return;
    }

    try {
      // Set loading state
      setThread((prev) => {
        if (!prev) return null;
        const updatedMessages = [...prev.messages];
        if (updatedMessages[messageIndex]) {
          (updatedMessages[messageIndex] as any)._loading = true;
          if (isRetry) {
            (updatedMessages[messageIndex] as any)._error = false;
            // Clear the error message on retry
            updatedMessages[messageIndex].body = undefined;
            updatedMessages[messageIndex].htmlBody = undefined;
          }
        }
        return { ...prev, messages: updatedMessages };
      });

      const message = thread.messages[messageIndex];
      const url = `/documents/email/stored/email_with_attachments/${message.id}`;
      const response = await apiClient.get<EmailMessage>(url, {
        withCredentials: true,
      });
      const details = response;

      setThread((prev) => {
        if (!prev) return null;
        const updatedMessages = [...prev.messages];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          body: details.body,
          htmlBody: details.htmlBody,
          attachments: details.attachments || [],
          mailAttachments: details.mailAttachments || [],
          tags: details.tags,
          metadata: details.metadata,
          extractedData: details.extractedData,
        };
        (updatedMessages[messageIndex] as any)._loading = false;
        return { ...prev, messages: updatedMessages };
      });
    } catch (err) {
      setThread((prev) => {
        if (!prev) return null;
        const updatedMessages = [...prev.messages];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          body: "Error Fetching Email 🥺 \n",
          attachments: [],
          mailAttachments: [],
        };
        (updatedMessages[messageIndex] as any)._loading = false;
        (updatedMessages[messageIndex] as any)._error = true;
        return { ...prev, messages: updatedMessages };
      });
      console.error("Load message content failed:", err);
    }
  };

  const retryFetchMessage = async (messageIndex: number) => {
    // Remove from loaded set to allow retry
    loadedMessagesRef.current.delete(messageIndex);
    await loadMessageContent(messageIndex, true);
  };

  if (initialThreadLoading) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...sx,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" color="error" sx={sx}>
        Error: {error}
      </Typography>
    );
  }

  if (!thread)
    return (
      <Typography variant="body1" sx={sx}>
        Thread not found
      </Typography>
    );

  const messages = thread.messages || [];
  const totalMessages = messages.length;
  const currentMessage = messages[currentIndex];

  // If no messages in thread, show a message (but only if we're not still loading)
  if (totalMessages === 0 && !initialThreadLoading) {
    return (
      <Typography variant="body1" color="textSecondary" sx={sx}>
        No messages found in this thread
      </Typography>
    );
  }

  return (
    <Box
      className="EmailThreadComponent"
      display="flex"
      sx={[{ overflowY: "auto", height: "100%" }, sx as any]}
      p={2}
    >
      <Box sx={{ flexGrow: 1, maxWidth: "100%" }}>
        {/* Show inbox email chip when viewing as admin */}
        {emailMetadata && (
          <Box mb={2}>
            <Chip
              label={`Viewing ${emailMetadata.inboxEmail}'s inbox`}
              color="primary"
              size="small"
              sx={{
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                borderColor: "rgba(25, 118, 210, 0.23)",
                color: "rgba(25, 118, 210, 0.87)",
                fontWeight: "bold",
              }}
            />
          </Box>
        )}

        <Typography variant="h5" fontSize={18} fontWeight="bold" mb={1} mt={0}>
          {thread.thread?.subject ||
            thread.messages[0]?.subject ||
            "No Subject"}
        </Typography>

        {/* Navigation & Counter */}
        <Box display="flex" alignItems="center" mb={2}>
          {/* Up/Down Buttons */}
          <IconButton
            color="primary"
            onClick={() => setCurrentIndex((prev) => prev - 1)}
            disabled={currentIndex === 0}
            sx={{ mr: 1 }}
          >
            <ArrowUpwardIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => setCurrentIndex((prev) => prev + 1)}
            disabled={currentIndex === totalMessages - 1}
          >
            <ArrowDownwardIcon />
          </IconButton>
          {/* Message Counter */}
          <Typography variant="caption" fontWeight="bold" sx={{ ml: 2 }}>
            Showing message {currentIndex + 1} / {totalMessages}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <DownloadFileButton
            name="Download Email"
            url={`${getServiceUrl()}/documents/email/download_eml/${currentMessage?.messageId}${emailMetadata ? `?inboxEmail=${encodeURIComponent(emailMetadata.inboxEmail)}` : ""}`}
          />
          {/* Admin-only Copy Metadata button */}
          {user?.isAdmin && (
            <IconButton
              color="primary"
              onClick={copyMetadata}
              sx={{ ml: 1 }}
              title="Copy Email Metadata"
            >
              <ContentCopyIcon />
            </IconButton>
          )}
        </Box>

        {/* Admin Panel (Admin Only) - Show before message */}
        {user?.isAdmin && currentMessage && (
          <AdminEmailPanel emailId={currentMessage.id!} />
        )}

        {/* Email Message */}
        <EmailMessageComponent
          message={currentMessage}
          onSelectForwardedEmail={setSelectedForwardedEmail}
          onRetryMessage={() => retryFetchMessage(currentIndex)}
          showMessageIds={false}
        />

        {/* Retry Button for Failed Messages */}
        {currentMessage && (currentMessage as any)._error && (
          <Box display="flex" justifyContent="center" mt={2}>
            <IconButton
              color="primary"
              onClick={() => retryFetchMessage(currentIndex)}
              disabled={(currentMessage as any)._loading}
              sx={{
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                border: "1px solid rgba(25, 118, 210, 0.5)",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Forwarded Email Modal */}
      {selectedForwardedEmail && (
        <Dialog
          open={Boolean(selectedForwardedEmail)}
          onClose={() => setSelectedForwardedEmail(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {selectedForwardedEmail.subject}
            <IconButton
              aria-label="close"
              onClick={() => setSelectedForwardedEmail(null)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <EmailMessageComponent
              message={selectedForwardedEmail}
              onSelectForwardedEmail={setSelectedForwardedEmail}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
